import { useMemo } from 'react'

const MEMBER_COLORS = {
  slate: '#64748b', rose: '#f43f5e', amber: '#f59e0b', emerald: '#10b981',
  sky: '#0ea5e9', violet: '#8b5cf6', pink: '#ec4899', orange: '#f97316',
  teal: '#14b8a6', indigo: '#6366f1',
}

function getUrgency(task) {
  if (!task.due_date) return 'normal'
  const diff = (new Date(task.due_date) - new Date()) / 86400000
  if (diff < 0) return 'overdue'
  if (diff < 1) return 'today'
  return 'normal'
}

export function ChoresTasksPanel({ data }) {
  const { tasks = [], members = [] } = data

  const memberMap = useMemo(() =>
    Object.fromEntries(members.map((m) => [m.id, m])),
    [members]
  )

  const pending = useMemo(() =>
    tasks
      .filter((t) => !t.completed_at)
      .sort((a, b) => {
        const ua = getUrgency(a), ub = getUrgency(b)
        const order = { overdue: 0, today: 1, normal: 2 }
        return (order[ua] ?? 2) - (order[ub] ?? 2)
      })
      .slice(0, 10),
    [tasks]
  )

  if (pending.length === 0) {
    return (
      <div className="flex flex-col h-full items-center justify-center panel-animate-in">
        <div className="text-5xl sm:text-6xl mb-3">✅</div>
        <p className="text-white text-xl sm:text-2xl font-bold mb-1">All caught up!</p>
        <p className="text-gray-400 text-base sm:text-lg">No pending tasks</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full px-4 sm:px-8 py-4 sm:py-6 panel-animate-in min-h-0">
      <p className="text-gray-500 text-xs sm:text-sm font-semibold uppercase tracking-widest mb-3">To Do</p>

      <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2 overflow-y-auto min-h-0 scrollbar-hide content-start">
        {pending.map((task) => {
          const urgency = getUrgency(task)
          const assignee = task.assigned_to ? memberMap[task.assigned_to] : null

          return (
            <div
              key={task.id}
              className={`flex items-start gap-2.5 rounded-xl px-3 py-2.5 ${
                urgency === 'overdue' ? 'bg-red-500/20 border border-red-500/40' :
                urgency === 'today'   ? 'bg-amber-500/20 border border-amber-500/40' :
                'bg-white/8'
              }`}
            >
              <div className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${
                urgency === 'overdue' ? 'bg-red-400' :
                urgency === 'today'   ? 'bg-amber-400' :
                'bg-gray-500'
              }`} />
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm sm:text-base leading-snug truncate">{task.title}</p>
                {task.due_date && (
                  <p className={`text-xs mt-0.5 ${urgency === 'overdue' ? 'text-red-400' : urgency === 'today' ? 'text-amber-400' : 'text-gray-500'}`}>
                    {urgency === 'overdue' ? 'Overdue' : urgency === 'today' ? 'Due today' : new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </p>
                )}
              </div>
              {assignee && (
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5"
                  style={{ backgroundColor: MEMBER_COLORS[assignee.memberColor] ?? '#8b5cf6' }}
                  title={assignee.displayName}
                >
                  {assignee.displayName?.[0]?.toUpperCase()}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
